import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  Lang: i18n.language,
});

const buildCompensationListParams = (page, filters = {}, perPage = 20) => {
  const params = { page, per_page: perPage };

  if (filters.compensation_number) {
    params.compensation_number = filters.compensation_number;
  }
  if (filters.travel_request_number) {
    params.travel_request_number = filters.travel_request_number;
  }
  const categoryType = filters.category_type || filters.type;
  if (categoryType) params.category_type = categoryType;
  if (filters.party) params.party = filters.party;
  if (filters.customer_type) params.customer_type = filters.customer_type;
  if (filters.customer_id) params.customer_id = filters.customer_id;
  if (filters.amount_from) params.amount_from = filters.amount_from;
  if (filters.amount_to) params.amount_to = filters.amount_to;
  if (filters.payment_status) params.payment_status = filters.payment_status;
  if (filters.created_by_user_id) {
    params.created_by_user_id = filters.created_by_user_id;
  }
  if (filters.updated_by_user_id) {
    params.updated_by_user_id = filters.updated_by_user_id;
  }
  if (filters.date_filter) params.date_filter = filters.date_filter;

  (filters.compensation_category_ids || []).forEach((id, index) => {
    if (id) params[`compensation_category_ids[${index}]`] = id;
  });
  (filters.statuses || []).forEach((status, index) => {
    if (status) params[`statuses[${index}]`] = status;
  });

  if (filters.date_filter === "range") {
    if (filters.date_value_from) params["date_value[0]"] = filters.date_value_from;
    if (filters.date_value_to) params["date_value[1]"] = filters.date_value_to;
  }

  return params;
};

/** GET /travel-request-compensations */
export const fetchTravelRequestCompensationsList = createAsyncThunk(
  "travelRequestCompensations/fetchList",
  async ({ page = 1, filters = {}, per_page = 20 } = {}, { rejectWithValue }) => {
    try {
      const params = buildCompensationListParams(page, filters, per_page);
      const response = await axios.get(`${BASE_URL}/travel-request-compensations`, {
        params,
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch"
      );
    }
  }
);

/** GET /travel-request-compensations/:travelRequestId */
export const fetchTravelRequestCompensationsByTrip = createAsyncThunk(
  "travelRequestCompensations/fetchByTrip",
  async (travelRequestId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/travel-request-compensations/${travelRequestId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to load trip compensations"
      );
    }
  }
);

/** POST /travel-request-compensations/:id/status */
export const updateTravelRequestCompensationStatus = createAsyncThunk(
  "travelRequestCompensations/updateStatus",
  async ({ compensationId, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);
      const response = await axios.post(
        `${BASE_URL}/travel-request-compensations/${compensationId}/status`,
        formData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update status"
      );
    }
  }
);

/** POST /travel-request-compensations/:travelRequestId */
export const saveTravelRequestCompensations = createAsyncThunk(
  "travelRequestCompensations/save",
  async ({ travelRequestId, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/travel-request-compensations/${travelRequestId}`,
        formData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to save compensations"
      );
    }
  }
);

/** GET /compensation-categories (all approved pages) */
export const fetchApprovedCompensationCategories = createAsyncThunk(
  "travelRequestCompensations/fetchCategories",
  async ({ type = "", forFilter = false } = {}, { rejectWithValue }) => {
    try {
      let page = 1;
      let lastPage = 1;
      const all = [];
      do {
        const params = {
          page,
          per_page: 50,
          status: "approved",
          deleted: "0",
        };
        if (type) params.type = type;
        const response = await axios.get(`${BASE_URL}/compensation-categories`, {
          params,
          headers: getAuthHeaders(),
        });
        if (!response.data?.status || !response.data?.data) break;
        all.push(...(response.data.data.data || []));
        lastPage = response.data.data.pagination?.last_page || 1;
        page += 1;
      } while (page <= lastPage);
      return { type: type || "", forFilter, rows: all };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to load categories"
      );
    }
  }
);

const initialState = {
  list: [],
  pagination: null,
  summary: null,
  isLoading: false,
  error: null,
  success: null,
  tripDetail: null,
  isLoadingTripDetail: false,
  isUpdatingStatus: false,
  isSaving: false,
  categories: [],
  filterCategories: [],
  isLoadingCategories: false,
  isLoadingFilterCategories: false,
};

const travelRequestCompensationsSlice = createSlice({
  name: "travelRequestCompensations",
  initialState,
  reducers: {
    clearTravelRequestCompensationsState: (state) => {
      state.error = null;
      state.success = null;
    },
    clearTravelRequestCompensationDetail: (state) => {
      state.tripDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTravelRequestCompensationsList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTravelRequestCompensationsList.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload?.data;
        state.list = data?.data || [];
        state.pagination = data?.pagination || null;
        state.summary = data?.summary || null;
      })
      .addCase(fetchTravelRequestCompensationsList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch";
      })

      .addCase(fetchTravelRequestCompensationsByTrip.pending, (state) => {
        state.isLoadingTripDetail = true;
      })
      .addCase(fetchTravelRequestCompensationsByTrip.fulfilled, (state, action) => {
        state.isLoadingTripDetail = false;
        if (action.payload?.status && action.payload?.data) {
          state.tripDetail = action.payload.data;
        }
      })
      .addCase(fetchTravelRequestCompensationsByTrip.rejected, (state, action) => {
        state.isLoadingTripDetail = false;
        state.error = action.payload || "Failed to load trip compensations";
      })

      .addCase(updateTravelRequestCompensationStatus.pending, (state) => {
        state.isUpdatingStatus = true;
        state.error = null;
      })
      .addCase(updateTravelRequestCompensationStatus.fulfilled, (state, action) => {
        state.isUpdatingStatus = false;
        state.success = action.payload?.message || null;
      })
      .addCase(updateTravelRequestCompensationStatus.rejected, (state, action) => {
        state.isUpdatingStatus = false;
        state.error = action.payload || "Failed to update status";
      })

      .addCase(saveTravelRequestCompensations.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveTravelRequestCompensations.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || null;
      })
      .addCase(saveTravelRequestCompensations.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || "Failed to save";
      })

      .addCase(fetchApprovedCompensationCategories.pending, (state, action) => {
        const forFilter = Boolean(action.meta.arg?.forFilter);
        if (forFilter) state.isLoadingFilterCategories = true;
        else state.isLoadingCategories = true;
      })
      .addCase(fetchApprovedCompensationCategories.fulfilled, (state, action) => {
        const forFilter = Boolean(action.meta.arg?.forFilter);
        const rows = action.payload?.rows || [];
        if (forFilter) {
          state.isLoadingFilterCategories = false;
          state.filterCategories = rows;
        } else {
          state.isLoadingCategories = false;
          state.categories = rows;
        }
      })
      .addCase(fetchApprovedCompensationCategories.rejected, (state, action) => {
        const forFilter = Boolean(action.meta.arg?.forFilter);
        if (forFilter) state.isLoadingFilterCategories = false;
        else state.isLoadingCategories = false;
        state.error = action.payload || "Failed to load categories";
      });
  },
});

export const {
  clearTravelRequestCompensationsState,
  clearTravelRequestCompensationDetail,
} = travelRequestCompensationsSlice.actions;

export default travelRequestCompensationsSlice.reducer;
